import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchPostById, incrementBlogPostView } from '../../fetchProjects';
import { sanitizeAndFormat } from '../../utils/sanitize';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const loadPost = async () => {
      setLoading(true);
      // Increment views without awaiting to not block page load
      incrementBlogPostView(id);
      
      const data = await fetchPostById(id);
      setPost(data);
      setLoading(false);
    };
    loadPost();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background text-text-primary flex flex-col items-center justify-center p-4">
        <Helmet>
          <meta name="robots" content="noindex" />
          <title>Post Not Found | Sudhan R</title>
        </Helmet>
        <h2 className="text-2xl font-bold mb-4 font-heading">Post Not Found</h2>
        <Link to="/blog">
          <Button variant="primary">Back to Blog</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent text-text-primary selection:bg-accent selection:text-white">
      <Helmet>
        <title>{post.title} | Sudhan R</title>
        <meta name="description" content={post.excerpt || `Read ${post.title} by ${post.author || 'Sudhan R'}`} />
        <link rel="canonical" href={`https://sudhan.live/blog/${post.slug || post.id}`} />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content={`${post.title} | Sudhan R`} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://sudhan.live/blog/${post.slug || post.id}`} />
        <meta property="og:image" content={post.image || "https://sudhan.live/assets/images/Sudhan_2026_v3.png"} />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author || "Sudhan R"} />
        {post.category && <meta property="article:section" content={post.category} />}
        {post.tags && post.tags.map((tag, idx) => (
          <meta property="article:tag" content={tag} key={idx} />
        ))}

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={post.image || "https://sudhan.live/assets/images/Sudhan_2026_v3.png"} />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BlogPosting",
                "@id": `https://sudhan.live/blog/${post.slug || post.id}#blogpost`,
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": `https://sudhan.live/blog/${post.slug || post.id}`
                },
                "headline": post.title,
                "description": post.excerpt,
                "image": post.image || "https://sudhan.live/assets/images/Sudhan_2026_v3.png",
                "datePublished": post.date,
                "dateModified": post.date,
                "author": {
                  "@id": "https://sudhan.live/#person"
                },
                "publisher": {
                  "@id": "https://sudhan.live/#person"
                },
                "keywords": post.tags ? post.tags.join(', ') : post.category
              },
              {
                "@type": "BreadcrumbList",
                "@id": `https://sudhan.live/blog/${post.slug || post.id}#breadcrumb`,
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://sudhan.live/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Blog",
                    "item": "https://sudhan.live/blog"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": post.title,
                    "item": `https://sudhan.live/blog/${post.slug || post.id}`
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>
      
      <Header />

      <main className="pt-24 pb-20">
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Back Button */}
          <button 
            onClick={() => navigate(-1)}
            aria-label="Return to previous page"
            title="Back to Blog"
            className="inline-flex items-center text-text-secondary hover:text-text-primary transition-colors mb-12 group"
          >
            <Icon name="ArrowLeft" size={18} className="mr-2 transform group-hover:-translate-x-1 transition-transform" />
            <span className="font-semibold tracking-wide uppercase text-sm">Back to Blog</span>
          </button>

          {/* Post Header */}
          <header className="mb-12">
             <div className="flex items-center gap-3 mb-6">
                <span className="px-3 py-1 bg-accent/10 text-accent rounded-full text-xs font-semibold uppercase tracking-wider border border-accent/20">
                  {post.category}
                </span>
                <span className="text-text-muted text-sm flex items-center gap-1 font-medium">
                  <Icon name="Clock" size={14} />
                  {post.readTime}
                </span>
                <span className="text-text-muted text-sm flex items-center gap-1 font-medium">
                  <Icon name="Calendar" size={14} />
                  {post.date}
                </span>
              </div>
              <div className="relative z-10">
              <h1 className="text-4xl sm:text-6xl font-black mb-10 leading-tight font-heading text-text-primary">
                {post.title}
              </h1>

              {/* Author Section */}
              <div className="flex items-center gap-4 p-6 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 shadow-lg">
                <Image 
                  src={post.authorImage} 
                  alt={post.author} 
                  className="w-12 h-12 rounded-full object-cover border-2 border-white/20 shadow-xl"
                />
                <div>
                  <p className="text-xs text-text-muted uppercase font-bold tracking-widest mb-0.5">Written By</p>
                  <p className="text-text-primary font-bold">{post.author}</p>
                </div>
              </div>
              </div>
          </header>

          {/* Featured Image */}
          <div className="relative aspect-video rounded-3xl overflow-hidden mb-16 border border-border shadow-card">
              <Image 
                src={post.image} 
                alt={post.title}
                className="w-full h-full object-cover"
              />
          </div>

          {/* Content */}
          <div className="prose prose-slate prose-lg max-w-none">
             <div className="text-xl text-text-secondary italic mb-12 border-l-4 border-accent pl-8 leading-relaxed font-medium">
                {post.excerpt}
             </div>
             {/* Post Content */}
             <div className="prose prose-invert prose-slate prose-lg max-w-none text-text-secondary leading-relaxed space-y-8 lg:text-xl">
               <div 
                 className="blog-content"
                 dangerouslySetInnerHTML={{ __html: sanitizeAndFormat(post.content) }} 
               />
             </div>
          </div>

          {/* Tags */}
          <div className="mt-16 pt-12 border-t border-border-muted">
              <h3 className="text-sm uppercase font-bold text-text-muted mb-4 tracking-widest px-1">Keywords</h3>
              <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, idx) => (
                    <span key={idx} className="px-4 py-2 bg-surface rounded-full text-sm font-semibold text-text-secondary border border-border hover:border-accent/40 hover:text-accent transition-colors shadow-subtle">
                      #{tag}
                    </span>
                  ))}
              </div>
          </div>

          {/* CTA / Newsletter */}
          <div className="mt-20 bg-white/5 backdrop-blur-xl p-12 rounded-3xl border border-white/10 text-center shadow-2xl relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-smooth"></div>
              <h2 className="text-3xl font-bold mb-4 text-text-primary font-heading relative z-10">Enjoyed this post?</h2>
              <p className="text-text-secondary mb-8 max-w-md mx-auto font-medium relative z-10">Get deep-tech insights and research updates delivered straight to your inbox.</p>
              <Link 
                to="/contact" 
                className="relative z-10"
                aria-label="Navigate to Contact Page"
                title="Get in touch to collaborate or discuss research"
              >
                 <Button variant="primary" size="lg" className="bg-accent hover:bg-accent-600 text-white shadow-xl shadow-accent/20">Get in Touch</Button>
              </Link>
          </div>

        </article>
      </main>
      <Footer />
    </div>
  );
};

export default BlogDetail;
